import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { BRAND_LIMITS } from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Field';
import { cn } from '../../../lib/cn';
import { GUIDELINE_PLACEHOLDER } from '../brandCopy';
import { addGuideline, GUIDELINE_RULE_TEXT, listAnnouncement, removeAt } from '../tagRules';

/**
 * The Writing guidelines table (spec:265; web.md §3.2): one column headed "Guideline", one rule per row
 * with its remove button, and an add box under the table (Enter or Add). The rules are the guideline
 * rules (300 characters, 50 rules, no duplicates); stored rules that break them (E4) are shown as they
 * are and can be removed. `onChange` fires only when a rule is added or removed. Plain table markup:
 * the app's Table is meant to be a whole card.
 *
 * `id` is the add box's: the field's `<label for>` names it. Read-only shows the rules only.
 */
export function GuidelinesTable({
  id,
  items,
  onChange,
  readOnly = false,
  disabled = false,
  describedBy,
}: {
  id: string;
  items: readonly string[];
  onChange: (next: string[]) => void;
  readOnly?: boolean;
  disabled?: boolean;
  describedBy?: string;
}) {
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const removeButtons = useRef(new Map<number, HTMLButtonElement>());
  const [focusAfterRemove, setFocusAfterRemove] = useState<number | null>(null);

  useEffect(() => {
    if (focusAfterRemove === null) return;
    const target =
      removeButtons.current.get(focusAfterRemove) ?? removeButtons.current.get(focusAfterRemove - 1) ?? input.current;
    target?.focus();
    setFocusAfterRemove(null);
  }, [focusAfterRemove, items]);

  const full = items.length >= BRAND_LIMITS.guidelines;
  const problemId = `${id}-problem`;

  const add = () => {
    const outcome = addGuideline(items, draft);
    if ('items' in outcome) {
      onChange(outcome.items);
      setAnnouncement(listAnnouncement.added(outcome.items[outcome.items.length - 1]));
      setDraft('');
      setProblem(null);
    } else if ('duplicate' in outcome) {
      setAnnouncement(listAnnouncement.duplicate(outcome.duplicate));
      setDraft('');
      setProblem(null);
    } else if (outcome.error === 'tooLong') {
      setProblem(GUIDELINE_RULE_TEXT.tooLong);
    } else if (outcome.error === 'full') {
      setProblem(GUIDELINE_RULE_TEXT.full);
    }
  };

  const remove = (index: number) => {
    const item = items[index];
    onChange(removeAt(items, index));
    setAnnouncement(listAnnouncement.removed(item));
    setProblem(null);
    setFocusAfterRemove(index);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    add();
  };

  const describedByInput = [describedBy, problem || full ? problemId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      <div className="overflow-hidden rounded-lg border border-line">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th scope="col" className="border-b border-line bg-page px-3 py-2 text-left text-xs font-medium text-ink-muted">
                Guideline
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td className="px-3 py-3 text-ink-faint">{readOnly ? 'Not provided' : 'No guidelines yet.'}</td>
              </tr>
            ) : (
              items.map((item, index) => (
                // By position and text: a stored list may hold the same rule twice (E4).
                <tr key={`${index}:${item}`} className="border-b border-line-soft last:border-b-0">
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-start justify-between gap-3">
                      <span className="min-w-0 whitespace-pre-wrap break-words text-ink">{item}</span>
                      {!readOnly && (
                        <Button
                          ref={(el) => {
                            if (el) removeButtons.current.set(index, el);
                            else removeButtons.current.delete(index);
                          }}
                          type="button"
                          variant="plain"
                          size="sm"
                          disabled={disabled}
                          onClick={() => remove(index)}
                          aria-label={`Remove guideline: ${item}`}
                          className="shrink-0 px-2"
                        >
                          <X className="h-4 w-4" aria-hidden />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="mt-3 flex gap-2">
          <Input
            ref={input}
            id={id}
            value={draft}
            disabled={disabled || full}
            placeholder={GUIDELINE_PLACEHOLDER}
            aria-describedby={describedByInput}
            aria-invalid={problem === GUIDELINE_RULE_TEXT.tooLong ? true : undefined}
            onChange={(e) => {
              setDraft(e.target.value);
              if (problem === GUIDELINE_RULE_TEXT.tooLong) setProblem(null);
            }}
            onKeyDown={onKeyDown}
            className="min-w-0 flex-1"
          />
          <Button type="button" variant="secondary" disabled={disabled || full} onClick={add} aria-label="Add guideline">
            Add
          </Button>
        </div>
      )}
      {!readOnly && (problem || full) && (
        <p
          id={problemId}
          className={cn(
            'mt-1 text-xs',
            problem === GUIDELINE_RULE_TEXT.tooLong ? 'text-red-600 dark:text-error-dark' : 'text-ink-muted',
          )}
        >
          {full ? GUIDELINE_RULE_TEXT.full : problem}
        </p>
      )}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}
